<template>
  <div id="edit_deal_buyers">
    <div class="form-group row mt-sm-3">
      <form-label :is-required="true">{{ capitalize($t('names.restrictions')) }} </form-label>
      <div class="col-sm-8">
        <div v-if="showDVBuyerWarning">
          <div class="alert alert-info">
            <icon-info />
            {{ buyerSelectionWarning }}
          </div>
        </div>
        <label
          v-if="!isNGOrPreferredDeal"
          class="control-label"
          for="edit_deal_available_to_all_hivestack_buyers">
          <input
            id="edit_deal_available_to_all_hivestack_buyers"
            v-model="selectedBuyerOptionInternal"
            type="radio"
            name="restrictDealToSpecificBuyers"
            :disabled="!canEditBuyersSection"
            :value="PackageBuyerOptions.AvailableToAllBuyers" />
          {{ formattedLabelForAllBuyers }}
        </label>
        <!-- TO BE REMOVED SOON -->
        <label
          v-if="isNGOrPreferredDeal && showExternalBuyerOption"
          class="control-label"
          for="edit_deal_available_to_all_external_buyers">
          <input
            id="edit_deal_available_to_all_external_buyers"
            v-model="selectedBuyerOptionInternal"
            type="radio"
            name="restrictDealToSpecificBuyers"
            :disabled="!canEditBuyersSection"
            :value="PackageBuyerOptions.AvailableToAllExternalBuyers" />
          {{ capitalize($t('messages.allExternalBuyers')) }}
        </label>
        <label
          v-if="showExternalExchangeBuyerOption && isNGOrPreferredDeal"
          class="control-label"
          for="edit_external_exchange_open_deals">
          <input
            id="edit_external_exchange_open_deals"
            v-model="selectedBuyerOptionInternal"
            type="radio"
            name="restrictDealToSpecificBuyers"
            :disabled="!canEditBuyersSection"
            :value="PackageBuyerOptions.AvailableToSpecificDsps" />
          {{ capitalize($t('messages.selectDSP')) }}
        </label>
        <label
          class="control-label"
          for="edit_restrict_deal_to_specific_buyers">
          <input
            id="edit_restrict_deal_to_specific_buyers"
            v-model="selectedBuyerOptionInternal"
            type="radio"
            name="restrictDealToSpecificBuyers"
            :disabled="!canEditBuyersSection"
            :value="PackageBuyerOptions.AvailableToSpecificBuyers" />
          {{ capitalize($t('messages.selectBuyers')) }}
        </label>
        <label
          v-if="isNGOrPreferredDeal && isSupplyOrganization"
          class="control-label"
          for="edit_restrict_deal_to_specific_curators">
          <input
            id="edit_restrict_deal_to_specific_curators"
            v-model="selectedBuyerOptionInternal"
            type="radio"
            name="restrictDealToSpecificCurators"
            :disabled="!canEditBuyersSection"
            :value="PackageBuyerOptions.AvailableToSpecificCurators" />
          {{ capitalize($t('messages.selectCurators')) }}
        </label>
      </div>
    </div>
    <div
      v-if="selectedBuyerOptionInternal === PackageBuyerOptions.AvailableToSpecificBuyers"
      class="form-group row">
      <form-label />
      <div
        id="deal_buyer_restrictions"
        class="col-sm-8">
        <multiselect
          v-model="selectedRestrictedBuyerOrganizationDSPsInternal"
          :options="buyerOrganizationDSPsForRestrictedDealsInternal"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :hide-selected="false"
          group-values="organizations"
          group-label="dspName"
          :group-select="true"
          label="displayName"
          track-by="id"
          :show-labels="false"
          :searchable="true">
          <template #singleLabel="props">
            <span class="option__desc flex-h">
              <span class="option__title flex-grow"
                >{{ props.option.name }}
                <span
                  v-if="props.option.seatID != null"
                  v-tooltip="'Seat ID'"
                  class="badge badge-disabled-hollow badge-md mr-3"
                  >{{ props.option.seatID }}</span
                >
              </span>
              <span class="badge badge-info badge-md">{{ getFormattedDSPName(props.option.dsp_name) }}</span>
            </span>
          </template>
          <template #option="props">
            <div
              v-if="props.option.$isLabel"
              class="option__desc flex-h">
              <span
                v-if="props.option.$isLabel"
                class="option__title flex-grow"
                >{{ getFormattedDSPName(props.option.$groupLabel) }}</span
              >
              <span class="badge badge-disabled-hollow badge-md">
                {{ getFormattedBuyersCountByGroup(props.option.$groupLabel) }}
              </span>
            </div>
            <div
              v-else
              class="option__desc flex-h">
              <span class="option__title flex-grow"
                >{{ props.option.name }}
                <span
                  v-if="props.option.seatID != null"
                  v-tooltip="'Seat ID'"
                  class="badge badge-disabled-hollow badge-md ml-3"
                  >{{ props.option.seatID }}</span
                >
              </span>
              <span class="badge badge-info badge-md">{{ getFormattedDSPName(props.option.dsp_name) }}</span>
            </div>
          </template>
          <template #tag="props">
            <span class="multiselect__tag">
              <span
                >{{ props.option.name }} -
                <small
                  ><small v-if="props.option.seatID != null">[{{ props.option.seatID }}] - </small>
                  {{ getFormattedDSPName(props.option.dsp_name) }}
                </small>
              </span>
              <i
                aria-hidden="true"
                tabindex="0"
                class="multiselect__tag-icon"
                @click="
                  selectedBuyerOrganizationDSPsInternal = selectedRestrictedBuyerOrganizationDSPsInternal.filter(
                    (x) => x.id !== props.option.id,
                  )
                " />
            </span>
          </template>
        </multiselect>
        <span
          v-show="errors.has('buyerOrganizationDSPs')"
          class="help is-danger"
          >{{ errors.first('buyerOrganizationDSPs') }}</span
        >
      </div>
    </div>
    <div
      v-if="selectedBuyerOptionInternal === PackageBuyerOptions.AvailableToSpecificCurators"
      class="form-group row">
      <form-label />
      <div
        id="deal_curator_restrictions"
        class="col-sm-8">
        <div>
          <selectable-expandable-small-tiles
            :id="'deal_curator_restrictions'"
            ref="selectableExpandableSmallTiles"
            :items="curatorOrganizations"
            is-disabled-property-name="isDisabled"
            :should-display-labels="true"
            :should-display-images="true"
            :show-view-more-as-tiles="true"
            :allow-sort="false"
            track-by-property="curatorOrganizationID"
            label-property-name="name"
            :max-items-to-show="maxItemsToShow"
            image-source-property-name="logoURL"
            :selected-items="selectedCuratorOrganizationsInternal"
            :search-enabled="props.curatorOrganizations.length > 0"
            :search-hint="capitalize($t('actions.searchCurators'))"
            :modal-title="capitalize($t('actions.browseCurators'))"
            @close-modal="emit('closeModal')"
            @selection-changed="(val) => emit('selectedCuratorOrganizationsChanged', val)" />
          <span
            v-show="errors.has('selectedCuratorOrganizations')"
            class="help is-danger"
            >{{ errors.first('selectedCuratorOrganizations') }}</span
          >
        </div>
      </div>
    </div>
    <div
      v-if="selectedBuyerOptionInternal === PackageBuyerOptions.AvailableToSpecificDsps"
      class="row">
      <form-label />
      <div
        id="deal_exchange_buyer_restrictions"
        class="col-sm-8">
        <div>
          <selectable-expandable-small-tiles
            :id="'deal_exchange_buyer_restrictions'"
            ref="selectableExpandableSmallTiles"
            :items="organizationExternalExchangesInternal"
            is-disabled-property-name="isDisabled"
            :should-display-labels="true"
            :should-display-images="true"
            :show-view-more-as-tiles="true"
            :allow-sort="false"
            track-by-property="externalExchangeID"
            label-property-name="name"
            :max-items-to-show="maxItemsToShow"
            image-source-property-name="logoURL"
            :selected-items="selectedBuyerExternalExchangesInternal"
            :search-enabled="organizationExternalExchanges.length > 0"
            :search-hint="capitalize($t('actions.searchDSPs'))"
            :modal-title="capitalize($t('actions.browseDSPs'))"
            @close-modal="emit('closeModal')"
            @selection-changed="(val) => emit('selectedBuyerExternalExchangesChanged', val)" />
          <span
            v-show="errors.has('selectedBuyerExternalExchanges')"
            class="help is-danger"
            >{{ errors.first('selectedBuyerExternalExchanges') }}</span
          >
        </div>
      </div>
      <form-label />
      <div
        v-if="isDV360ExchangeSelected || isXandrExchangeSelected || isBellExchangeSelected"
        class="col-sm-8">
        <div class="alert alert-info">
          <icon-info />
          {{ buyerSelectionWarning }}
        </div>
      </div>
      <form-label />
      <div
        v-if="isDV360ExchangeSelected || isXandrExchangeSelected"
        id="xandr_dv_deal_buyer_restrictions"
        class="col-sm-8 focus">
        <multiselect
          v-model="selectedRestrictedBuyerOrganizationDSPsInternal"
          :options="buyerOrganizationDSPsForRestrictedDealsInternal"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :hide-selected="false"
          group-values="organizations"
          group-label="dspName"
          :group-select="true"
          label="displayName"
          track-by="id"
          :show-labels="false"
          :searchable="true">
          <template #singleLabel="props">
            <span class="option__desc flex-h">
              <span class="option__title flex-grow"
                >{{ props.option.name }}
                <span
                  v-if="props.option.seatID != null"
                  v-tooltip="'Seat ID'"
                  class="badge badge-disabled-hollow badge-md mr-3"
                  >{{ props.option.seatID }}</span
                >
              </span>
              <span class="badge badge-info badge-md">{{ getFormattedDSPName(props.option.dsp_name) }}</span>
            </span>
          </template>
          <template #option="props">
            <div
              v-if="props.option.$isLabel"
              class="option__desc flex-h">
              <span
                v-if="props.option.$isLabel"
                class="option__title flex-grow"
                >{{ getFormattedDSPName(props.option.$groupLabel) }}</span
              >
              <span class="badge badge-disabled-hollow badge-md">
                {{ getFormattedBuyersCountByGroup(props.option.$groupLabel) }}
              </span>
            </div>
            <div
              v-else
              class="option__desc flex-h">
              <span class="option__title flex-grow"
                >{{ props.option.name }}
                <span
                  v-if="props.option.seatID != null"
                  v-tooltip="'Seat ID'"
                  class="badge badge-disabled-hollow badge-md ml-3"
                  >{{ props.option.seatID }}</span
                >
              </span>
              <span class="badge badge-info badge-md">{{ getFormattedDSPName(props.option.dsp_name) }}</span>
            </div>
          </template>
          <template #tag="props">
            <span class="multiselect__tag">
              <span
                >{{ props.option.name }} -
                <small
                  ><small v-if="props.option.seatID != null">[{{ props.option.seatID }}] - </small>
                  {{ getFormattedDSPName(props.option.dsp_name) }}
                </small>
              </span>
              <i
                aria-hidden="true"
                tabindex="0"
                class="multiselect__tag-icon"
                @click="
                  selectedBuyerOrganizationDSPsInternal = selectedRestrictedBuyerOrganizationDSPsInternal.filter(
                    (x) => x.id !== props.option.id,
                  )
                " />
            </span>
          </template>
        </multiselect>
        <span
          v-show="errors.has('buyerOrganizationDSPs')"
          class="help is-danger"
          >{{ errors.first('buyerOrganizationDSPs') }}</span
        >
      </div>
    </div>
    <template v-if="isNGExternalOrPreferredExternalDeal">
      <span
        v-if="isMissingAllSelectedExchange && organizationExternalExchanges.length === 0"
        class="alert alert-warning row">
        {{ capitalize(i18n.global.t('messages.noDSPsAvailable')) }}
      </span>
      <span
        v-else-if="isMissingSomeSelectedExchange"
        class="alert alert-warning row">
        {{ capitalize(i18n.global.t('messages.someSelectedDSPsareMissing')) }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, inject, onUnmounted, Ref, ref, toRefs, watch } from 'vue';
  import { Multiselect } from 'vue-multiselect';

  import { FormLabel, SelectableExpandableSmallTiles } from '@/common/components/controls';
  import { DealScope } from '@/common/components/deals/DealsHelper';
  import { IconInfo } from '@/common/icons/monochrome';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import ExternalExchange from '@/common/models/misc-api-models/ExternalExchange';
  import { OrganizationDSPs, OrganizationDSPsOrganization } from '@/common/models/misc-api-models/OrganizationDSP';
  import PackageBuyerOptions from '@/common/models/package/PackageBuyerOptions';
  import { mapOrgAppState } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';
  import i18n from '@/vueI18n';

  type ExternalExchangesInternal = ExternalExchange & {
    isDisabled: boolean;
  };

  interface IEditDealBuyerOptionProps {
    buyerOrganizationDSPs: OrganizationDSPs[];
    buyerOrganizationDSPsForRestrictedDeals: OrganizationDSPs[];
    canEditBuyersSection?: boolean;
    isBellBuyerSelected?: boolean;
    isBellExchangeSelected?: boolean;
    isDV360BuyerSelected?: boolean;
    isDV360ExchangeSelected?: boolean;
    isEvergreenDeal?: boolean;
    isExternalDSPSelected?: boolean;
    isInternalDSPSelected?: boolean;
    // To ensure we can't select certain internal/external DSPs/buyers
    dealLockedScopeType?: DealScope;
    isMissingAllSelectedExchange: boolean;
    isMissingSomeSelectedExchange: boolean;
    isNew?: boolean;
    isNGExternalOrPreferredExternalDeal?: boolean;
    isNGOrPreferredDeal?: boolean;
    isPGDeal?: boolean;
    isXandrBuyerSelected?: boolean;
    isXandrExchangeSelected?: boolean;
    curatorOrganizations: CuratorOrganization[];
    organizationExternalExchanges: ExternalExchangesInternal[];
    selectedBuyerExternalExchanges: ExternalExchangesInternal[];
    selectedBuyerOption: string;
    selectedBuyerOrganizationDSPs: OrganizationDSPs[];
    selectedRestrictedBuyerOrganizationDSPs: OrganizationDSPs[];
    selectedCuratorOrganizations: CuratorOrganization[];
    isSupplyOrganization: boolean;
    showDVBuyerWarning?: boolean;
  }

  // Props
  const props = withDefaults(defineProps<IEditDealBuyerOptionProps>(), {
    canEditBuyersSection: true,
    isBellBuyerSelected: false,
    isBellExchangeSelected: false,
    isDV360BuyerSelected: false,
    isDV360ExchangeSelected: false,
    isEvergreenDeal: false,
    isExternalDSPSelected: false,
    isInternalDSPSelected: false,
    dealLockedScopeType: null,
    isNew: false,
    isNGExternalOrPreferredExternalDeal: false,
    isNGOrPreferredDeal: false,
    isPGDeal: false,
    isXandrBuyerSelected: false,
    isXandrExchangeSelected: false,
    showDVBuyerWarning: false,
  });

  const {
    buyerOrganizationDSPsForRestrictedDeals,
    buyerOrganizationDSPs,
    canEditBuyersSection,
    organizationExternalExchanges,
    selectedBuyerOption,
    selectedBuyerOrganizationDSPs,
    selectedRestrictedBuyerOrganizationDSPs,
    selectedBuyerExternalExchanges,
    selectedCuratorOrganizations,
    isExternalDSPSelected,
    isInternalDSPSelected,
    dealLockedScopeType,
    isDV360ExchangeSelected,
    isXandrExchangeSelected,
    isBellExchangeSelected,
    isDV360BuyerSelected,
    isXandrBuyerSelected,
    isBellBuyerSelected,
    isEvergreenDeal,
    isNew,
    isNGExternalOrPreferredExternalDeal,
    isNGOrPreferredDeal,
    isPGDeal,
    isMissingAllSelectedExchange,
    isMissingSomeSelectedExchange,
    isSupplyOrganization,
    showDVBuyerWarning,
  } = toRefs(props);

  // Data
  const selectedBuyerOptionInternal: Ref<string> = ref(selectedBuyerOption.value);
  const selectedRestrictedBuyerOrganizationDSPsInternal = ref(selectedRestrictedBuyerOrganizationDSPs.value);
  const selectedBuyerExternalExchangesInternal = ref(selectedBuyerExternalExchanges.value);
  const selectedBuyerOrganizationDSPsInternal = ref(selectedBuyerOrganizationDSPs.value);
  const selectedCuratorOrganizationsInternal = ref(selectedCuratorOrganizations.value);
  const maxItemsToShow = ref(12);
  const errors: Ref<MessageCollection> = inject('errors', null);

  // Computed
  const { organization } = mapOrgAppState();

  const isInternallyLocked = computed(() => {
    return dealLockedScopeType.value === DealScope.INTERNAL;
  });

  const isExternallyLocked = computed(() => {
    return dealLockedScopeType.value === DealScope.EXTERNAL;
  });

  const organizationExternalExchangesInternal = computed<ExternalExchangesInternal[]>(() => {
    const getConditions = (exchange: ExternalExchangesInternal) => {
      if (isInternalDSPSelected.value || isInternallyLocked.value) {
        return exchange.code === 'INTERNAL';
      }
      if (isExternalDSPSelected.value) {
        return (
          exchange.code !== 'INTERNAL' &&
          !exchange.code.includes('DV360') &&
          !exchange.code.toLowerCase().includes('bell') &&
          !exchange.code.includes('XANDR')
        );
      }
      if (isExternallyLocked.value) {
        return exchange.code !== 'INTERNAL';
      }
      if (isDV360ExchangeSelected.value) {
        return exchange.code.includes('DV360');
      }
      if (isXandrExchangeSelected.value) {
        return exchange.code.includes('XANDR');
      }
      if (isBellExchangeSelected.value) {
        return exchange.code.toLowerCase().includes('bell');
      }
      return true;
    };
    return organizationExternalExchanges.value.map(
      (x) => ({ ...x, isDisabled: !getConditions(x) }) as ExternalExchangesInternal,
    ) as ExternalExchangesInternal[];
  });

  const buyerOrganizationDSPsForRestrictedDealsInternal = computed(() => {
    const getConditions = (org: OrganizationDSPsOrganization) => {
      const isInternalExchangeSelected =
        (selectedRestrictedBuyerOrganizationDSPs.value.some((x) => x.exchange === 'internal') ||
          isInternallyLocked.value) &&
        org.exchange === 'external';
      const isExternalExchangeSelected =
        (selectedRestrictedBuyerOrganizationDSPs.value.some((x) => x.exchange === 'external') ||
          isExternallyLocked.value) &&
        org.exchange === 'internal';
      const isXandrBell = ['xandr', 'bell'].some((buyer) => org.dsp_name.toLowerCase().includes(buyer));
      /**
       * 1. If it's PG, ensure that Xandr/Bell are all disabled since they are not supported
       * 2. If it's PG and evergreen, we want to ignore the exchange condition and disable Xandr/Bell
       * 3. In any other cases, look for mismatched exchanges, or if it's evergreen, add the same condition as above
       */
      if (isPGDeal.value || (isPGDeal.value && isEvergreenDeal.value)) {
        return isXandrBell;
      } else {
        return isExternalExchangeSelected || isInternalExchangeSelected || (isEvergreenDeal.value && isXandrBell);
      }
    };
    return buyerOrganizationDSPsForRestrictedDeals.value.map((buyers) => {
      const organizations = buyers.organizations.map((org) => {
        return { ...org, $isDisabled: getConditions(org) } as OrganizationDSPsOrganization;
      });
      return { ...buyers, organizations };
    });
  });

  const formattedLabelForAllBuyers = computed(() =>
    organization?.name === 'LIVE BOARD'
      ? capitalize(i18n.global.t('messages.liveBoardDSPBuyers'))
      : capitalize(i18n.global.t('messages.hivestackDSPBuyers')),
  );

  const buyerSelectionWarning = computed(() => {
    let exchangeName = null;
    if (isDV360ExchangeSelected.value) {
      exchangeName = 'DV360';
    } else if (isXandrExchangeSelected.value) {
      exchangeName = 'Xandr';
    } else if (isBellExchangeSelected.value) {
      exchangeName = 'Bell';
    }
    return exchangeName != null ? capitalize(i18n.global.t('messages.buyerSelectionWarning', [exchangeName])) : null;
  });

  const showExternalExchangeBuyerOption = computed(() => {
    if (isNGOrPreferredDeal.value) {
      windowResize();

      window.addEventListener('resize', windowResize);
      return organizationExternalExchanges.value.length > 0;
    }
    return false;
  });

  const showExternalBuyerOption = computed(
    () => !(!isNew.value || selectedBuyerOptionInternal.value !== PackageBuyerOptions.AvailableToAllExternalBuyers),
  );

  // Emits
  const emit = defineEmits<{
    (e: 'selectedBuyerOptionChanged', selectedBuyerOptionInternal): void;
    (e: 'selectedRestrictedBuyerOrganizationDSPsChanged', selectedRestrictedBuyerOrganizationDSPsInternal): void;
    (e: 'selectedBuyerExternalExchangesChanged', selectedBuyerExternalExchangesInternal): void;
    (e: 'selectedBuyerOrganizationDSPsChanged', selectedBuyerOrganizationDSPsInternal): void;
    (e: 'selectedCuratorOrganizationsChanged', selectedCuratorOrganizationsInternal): void;
    (e: 'closeModal'): void;
  }>();

  // watch

  watch(selectedBuyerOptionInternal, (val) => {
    if (val !== selectedBuyerOption.value) {
      emit('selectedBuyerOptionChanged', val);
    }
  });

  watch(selectedBuyerOption, (val) => {
    selectedBuyerOptionInternal.value = val;
  });

  watch(selectedRestrictedBuyerOrganizationDSPsInternal, (val) => {
    if (val !== selectedRestrictedBuyerOrganizationDSPs.value) {
      emit('selectedRestrictedBuyerOrganizationDSPsChanged', val);
    }
  });

  watch(selectedRestrictedBuyerOrganizationDSPs, (val) => {
    selectedRestrictedBuyerOrganizationDSPsInternal.value = val;
  });

  watch(selectedBuyerExternalExchangesInternal, (val) => {
    if (val !== selectedBuyerExternalExchanges.value) {
      emit('selectedBuyerExternalExchangesChanged', val);
    }
  });

  watch(selectedBuyerOrganizationDSPsInternal, (val) => {
    if (val !== selectedBuyerOrganizationDSPs.value) {
      emit('selectedBuyerOrganizationDSPsChanged', val);
    }
  });

  watch(selectedCuratorOrganizationsInternal, (val) => {
    if (val !== selectedCuratorOrganizations.value) {
      emit('selectedCuratorOrganizationsChanged', val);
    }
  });

  watch(selectedBuyerExternalExchanges, (val) => {
    selectedBuyerExternalExchangesInternal.value = val;
  });

  watch(selectedCuratorOrganizations, (val) => {
    selectedCuratorOrganizationsInternal.value = val;
  });

  // Methods
  const getFormattedDSPName = (dspName) => {
    let formattedName = dspName;
    if (organization?.name === 'LIVE BOARD') {
      formattedName = formattedName.replace('Hivestack', 'Live Board');
    }
    return formattedName;
  };

  const getFormattedBuyersCountByGroup = (dspName) => {
    if (buyerOrganizationDSPs.value != null) {
      const buyerOrganizationGroup = buyerOrganizationDSPs.value.find((x) => x.dspName === dspName);
      const buyerCount = buyerOrganizationGroup?.organizations?.length || 0;
      const buyerString =
        buyerCount === 1 ? i18n.global.t('names.buyer', { n: 1 }) : i18n.global.t('names.buyer', { n: 2 });
      return `${buyerCount} ${buyerString}`;
    }
    return null;
  };

  function windowResize() {
    const width = document.getElementById('edit_deal_buyers')?.offsetWidth || 0;
    maxItemsToShow.value = 7;
    if (width > 960) {
      maxItemsToShow.value = width > 1279 ? 11 : 8;
    }
  }

  onUnmounted(() => {
    window.removeEventListener('resize', windowResize);
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .curator-restrictions-warning {
    margin-top: 1rem !important;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  #edit_deal_buyers {
    width: 100%;

    .selectable-expandable-small-tiles {
      justify-content: center !important;
    }
  }

  .alert-info {
    svg {
      height: 1.4rem;
      width: 2.4rem;
    }
  }

  #xandr_dv_deal_buyer_restrictions.focus {
    .multiselect {
      border: 0.1rem solid var(--info);
      border-color: var(--info);
    }
  }
</style>
